import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';

// components
import ChatBubble from 'eCoach/ChatBubble';
import ClickableInput from 'eCoach/ClickableInput';

// styles
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import { UserInputType } from './utils';

interface AlternativeOption {
  action: () => void;
  text: string;
}

interface ECoachConfirmationInputProps {
  sendUserInput: (text: string, type: number) => void;
  inputType: number;
  alternativeOption?: AlternativeOption;
  positiveText?: string;
  negativeText?: string;
}

export default function ECoachConfirmationInput({
  sendUserInput,
  inputType,
  alternativeOption,
  positiveText = 'Yes',
  negativeText = 'No',
}: ECoachConfirmationInputProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const chat = usePosthogCapture();

  const captureConfirmation = useCallback(() => {
    switch (inputType) {
      case UserInputType.CHALLENGE_VERIFICATION_CONFIRMATION_ANSWER:
        chat.messageChallengeVerification({
          step: 'CHALLENGE_VERIFICATION_CONFIRMATION_ANSWER',
        });
        break;
      case UserInputType.RESOLUTION_TAKEN_ACTION_ANSWER:
        chat.messageResolutionSubmitted({
          step: 'RESOLUTION_TAKEN_ACTION_ANSWER',
        });
        break;
      case UserInputType.RESOLUTION_IS_RESOLVED_ANSWER:
        chat.messageResolutionSubmitted({
          step: 'RESOLUTION_IS_RESOLVED_ANSWER',
        });
        break;
      default:
        break;
    }
  }, [inputType, chat]);

  const onConfirm = useCallback(() => {
    sendUserInput(t(positiveText), inputType);
    captureConfirmation();
  }, [captureConfirmation, inputType, positiveText, sendUserInput, t]);

  const onDecline = useCallback(() => {
    sendUserInput(t(negativeText), inputType);
    captureConfirmation();
  }, [captureConfirmation, inputType, negativeText, sendUserInput, t]);

  const onAlternative = useCallback(() => {
    alternativeOption?.action();
    sendUserInput(t('Skip'), inputType);
  }, [alternativeOption, inputType, sendUserInput, t]);

  return (
    <ChatBubble sentByUser>
      <StyledPromptControls.ButtonsColumn>
        <ClickableInput
          inputAction={onConfirm}
          inputText={t(positiveText)}
          inputType="light"
        />
        <ClickableInput
          inputAction={onDecline}
          inputText={t(negativeText)}
          inputType="light"
        />
        {alternativeOption && (
          <ClickableInput
            inputAction={onAlternative}
            inputText={alternativeOption.text}
            inputType="light"
          />
        )}
      </StyledPromptControls.ButtonsColumn>
    </ChatBubble>
  );
}
